import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { SUPPORT_PHONE, SUPPORT_TEL } from '../config/site';
import { useHelp } from './HelpDialog';
import Icon from './Icon';
import { QuickExitLink } from './QuickExit';

const NAV = [
  { to: '/#como-funciona', label: 'Cómo funciona', router: false },
  { to: '/#preguntas', label: 'Preguntas', router: false },
  { to: '/politica-de-privacidad', label: 'Privacidad', router: true },
  { to: '/comentarios', label: 'Contacto', router: true },
];

const MENU = [
  { to: '/', label: 'Inicio' },
  { to: '/#como-funciona', label: 'Cómo funciona' },
  { to: '/#preguntas', label: 'Preguntas frecuentes' },
  { to: '/ayuda', label: 'Líneas de ayuda' },
  { to: '/politica-de-privacidad', label: 'Privacidad' },
  { to: '/comentarios', label: 'Contacto' },
  { to: '/actualizar', label: 'Actualizar la app' },
];

const Header: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const header = useRef<HTMLElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const help = useHelp();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // A new page always starts with the menu closed.
  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  // While the menu is open the page behind it cannot be reached (focus, keyboard, screen reader).
  useEffect(() => {
    document.body.classList.toggle('menu-open', open);
    document.querySelectorAll('[data-inert-when-menu]').forEach((el) => {
      if (open) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
    });
    if (open) {
      const top = Math.max(0, header.current?.getBoundingClientRect().bottom ?? 0);
      menu.current?.style.setProperty('--menu-top', `${top}px`);
      menu.current?.querySelector<HTMLElement>('a, button')?.focus();
    }
    return () => {
      document.body.classList.remove('menu-open');
      document.querySelectorAll('[data-inert-when-menu]').forEach((el) => el.removeAttribute('inert'));
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        toggle.current?.focus();
      }
    };
    const wide = window.matchMedia('(min-width: 1180px)');
    const onWide = (event: MediaQueryListEvent) => event.matches && setOpen(false);
    document.addEventListener('keydown', onKeyDown);
    wide.addEventListener('change', onWide);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      wide.removeEventListener('change', onWide);
    };
  }, [open]);

  const openHelp = (event: React.MouseEvent) => {
    event.preventDefault();
    setOpen(false);
    help.open();
  };

  return (
    <>
      <header ref={header} className={scrolled ? 'site-header is-scrolled' : 'site-header'}>
        <div className="container header__inner">
          <Link className="brand" to="/" aria-label="Violeta, ir al inicio">
            <img src="/assets/logo-96.png" width="44" height="44" alt="" />
            <span className="brand__name">Violeta</span>
          </Link>

          <nav className="nav" aria-label="Principal">
            <ul>
              {NAV.map((item) => (
                <li key={item.to}>
                  {item.router ? (
                    <NavLink to={item.to}>{item.label}</NavLink>
                  ) : (
                    <Link to={item.to}>{item.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="header__actions">
            <a className="btn btn--pink btn--sm" href="/ayuda" onClick={openHelp}>
              <Icon name="heart" />
              <span>
                Ayuda<span className="hide-sm"> ahora</span>
              </span>
            </a>
            <Link className="btn btn--light btn--sm hide-tablet" to="/#descargar">
              Descargar
            </Link>
            <QuickExitLink short />
            <button
              ref={toggle}
              className="menu-toggle"
              type="button"
              aria-expanded={open}
              aria-controls="menu-movil"
              onClick={() => setOpen((value) => !value)}
            >
              <Icon name={open ? 'x' : 'menu'} />
              <span className="sr-only">{open ? 'Cerrar menú' : 'Abrir menú'}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mobile-menu" id="menu-movil" ref={menu} hidden={!open}>
        <nav aria-label="Menú">
          <ul>
            {MENU.map((item, index) => (
              <li key={item.to}>
                <Link to={item.to}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="mobile-menu__foot">
          <Link className="btn btn--crimson btn--lg btn--block" to="/#descargar">
            <Icon name="download" />
            Descargar la app
          </Link>
          <a className="btn btn--outline-light btn--lg btn--block" href="/ayuda" onClick={openHelp}>
            <Icon name="heart" />
            Ayuda ahora
          </a>
          <QuickExitLink className="btn btn--outline-light btn--lg btn--block" />
        </div>
        <p className="mobile-menu__note">
          ¿Dudas sobre la app? Llama al <a href={SUPPORT_TEL}>{SUPPORT_PHONE}</a>. No es una línea de emergencias.
        </p>
      </div>
    </>
  );
};

export default Header;
